import { useTranslation } from "react-i18next";
import { useFirestoreDoc } from "../firebase/listeners.js";
import AspectCard from "./AspectCard.js";
import FpTracker from "./FpTracker.js";
import StressTrack from "./StressTrack.js";

interface Aspect {
  id: string;
  text: string;
  kind: string;
  visibility: "revealed" | "hidden";
}

interface Character {
  uid: string;
  name: string;
  aspects?: Aspect[];
  skills?: Record<string, number>;
  refresh: number;
  fp: number;
  stressState?: Record<string, boolean[]>;
}

export default function CharacterSheet({
  tableId,
  charId,
  isGM = false,
}: { tableId: string; charId: string; isGM?: boolean }) {
  const { t } = useTranslation();
  const { data, loading, error } = useFirestoreDoc<Character>(
    `tables/${tableId}/characters/${charId}`,
  );
  if (loading) return <p className="p-4">{t("app.loading")}</p>;
  if (error) return <p className="p-4 text-destructive">{error.message}</p>;
  if (!data) return <p className="p-4">not found</p>;

  return (
    <article className="p-4 space-y-4">
      <h1 className="text-2xl font-bold">{data.name}</h1>

      <section>
        <h2 className="text-sm font-semibold uppercase text-muted-foreground">
          {t("sheet.aspects")}
        </h2>
        <div className="mt-1 flex flex-wrap gap-2">
          {(data.aspects ?? []).map((a) => (
            <AspectCard key={a.id} aspect={a} isGM={isGM} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase text-muted-foreground">
          {t("sheet.skills")}
        </h2>
        <ul className="mt-1 space-y-0.5 font-mono text-sm">
          {Object.entries(data.skills ?? {})
            .sort(([, a], [, b]) => b - a)
            .map(([id, rank]) => (
              <li key={id}>
                <span className="text-muted-foreground">{id}</span>{" "}
                <span className="float-right">{rank >= 0 ? `+${rank}` : rank}</span>
              </li>
            ))}
        </ul>
      </section>

      <section className="space-y-2">
        <FpTracker current={data.fp} refresh={data.refresh} />
        {Object.entries(data.stressState ?? {}).map(([trackId, boxes]) => (
          <StressTrack key={trackId} label={trackId} boxes={boxes ?? []} />
        ))}
      </section>
    </article>
  );
}
